// Is the person filing the claim the same person the claim is for?
//
// Nobody can claim for their own death. The deceased is already excluded from the
// claimant dropdown, but the claimant fields are free text, so the details can
// still be typed in by hand — which is the case this catches.
//
// Kept in lib/ for the same reason as claimBenefitCaps and existingClaimGuard: the
// rule belongs to the product, not to one partner's screen, and the customer-facing
// journey applies it too.
//
// No optional chaining / ES6+ syntax the project's Babel config rejects.

import { getFullName } from './personDetails.es6'
import { nationalPhoneNumber } from './phoneNumbers.es6'

export const CLAIMANT_IS_DECEASED_MESSAGE =
  'The claimant details match the deceased. The deceased and the claimant cannot ' +
  'be the same person.'

function normalizeText(value) {
  return String(value === null || value === undefined ? '' : value)
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

/**
 * Compare two names as sets of words rather than as raw strings.
 *
 * Order does not matter: "MWANGI JOHN" is the same person as "JOHN MWANGI".
 *
 * Neither does a middle name the other side omits. The deceased's name is read
 * with getFullName, which includes a middle name when the record has one, while a
 * claimant filling the form in by hand generally types first and last — so an
 * exact comparison would miss precisely the person this guard is looking for. One
 * name being contained in the other counts as a match, provided the shorter one is
 * at least two words: a lone shared first name is far too weak to act on.
 */
function sameName(a, b) {
  const left = normalizeText(a)
  const right = normalizeText(b)
  if (!left || !right) {
    return false
  }
  if (left === right) {
    return true
  }

  const leftWords = left.split(' ')
  const rightWords = right.split(' ')
  const shorter = leftWords.length <= rightWords.length ? leftWords : rightWords
  const longer = leftWords.length <= rightWords.length ? rightWords : leftWords
  if (shorter.length < 2) {
    return false
  }
  for (let i = 0; i < shorter.length; i += 1) {
    if (longer.indexOf(shorter[i]) === -1) {
      return false
    }
  }
  return true
}

function sameIdentifier(a, b) {
  const left = normalizeText(a)
  const right = normalizeText(b)
  return !!left && !!right && left === right
}

function samePhone(a, b) {
  const left = nationalPhoneNumber(a)
  const right = nationalPhoneNumber(b)
  return !!left && !!right && left === right
}

/**
 * Does the claimant look like the deceased?
 *
 * Each arm is independent and only fires when BOTH sides have the value, so a
 * missing phone or ID never blocks a genuine claim. The name is read with
 * getFullName so a deceased carrying a middle name, a title, or only a `full_name`
 * field is compared on the same string the UI shows everywhere else — reading
 * first_name + last_name directly missed all three.
 *
 * `blocking` says whether the match is strong enough to stop the claim:
 *
 *   - name and ID number are entered deliberately and identify a person, so a
 *     match there blocks submission;
 *   - a phone number does not identify a person, it identifies a handset. The
 *     claimant form auto-fills it from the member record on selection, so on a
 *     family policy whose dependants all carry the household's one contact number,
 *     picking ANY claimant would reproduce the deceased's number without the user
 *     typing a thing. Blocking on that would strand the whole claim with no way
 *     round it, so a phone match is reported and shown, but does not block.
 *
 * @param {object} deceased  the covered life the claim is for (a dependant record)
 * @param {object} claimant  {fullName, idNumber, phone} as captured on the form
 * @returns {{match: boolean, on: (string|null), blocking: boolean}} `on` names the
 *          field that matched, for a message that says which detail to correct.
 */
export function claimantMatchesDeceased(deceased, claimant) {
  const noMatch = { match: false, on: null, blocking: false }
  if (!deceased || !claimant) {
    return noMatch
  }

  if (sameName(getFullName(deceased), claimant.fullName)) {
    return { match: true, on: 'name', blocking: true }
  }
  if (sameIdentifier(deceased.national_id, claimant.idNumber)) {
    return { match: true, on: 'ID number', blocking: true }
  }
  if (samePhone(deceased.msisdn || deceased.phone_number, claimant.phone)) {
    return { match: true, on: 'phone number', blocking: false }
  }
  return noMatch
}

/**
 * The message shown when the claimant looks like the deceased.
 *
 * A non-blocking match is worded as something to check rather than something that
 * is wrong, because on a shared household number it usually is not wrong.
 */
export function claimantIsDeceasedMessage(on, blocking) {
  if (!on) {
    return CLAIMANT_IS_DECEASED_MESSAGE
  }
  if (blocking === false) {
    return (
      `The claimant ${on} is the same as the deceased’s. That is expected on a shared household ` +
      `number — check it belongs to the claimant before continuing.`
    )
  }
  return `The claimant ${on} matches the deceased. The deceased and the claimant cannot be the same person.`
}
